import React from 'react';

import { TrendingUp, UserPlus, ShoppingCart } from 'lucide-react';

import AdminLayout from '../../components/layout/AdminLayout';

// NOTE: every figure on this page is scaffold placeholder content inherited from the project
// template. "Total Sales", "Completed Orders" and the order rows below are e-commerce, not golf.
// This pass restyles them onto the brand; it does not invent GolfPlayed metrics, because which
// numbers belong here is a product decision, not a styling one. See the handover.

interface MetricProps {
  label: string;
  value: string;
  delta: string;
  icon: React.ReactNode;
}

// One card shape, matching components/common/Card.tsx: white surface, hairline border, and a gold
// rule under the heading. Gold is the rule and the icon; it never carries the text, which on this
// light ground would be 2.32:1.
const Metric: React.FC<MetricProps> = ({ label, value, delta, icon }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
    <div className="flex items-center justify-between gap-4">
      <h2 className="font-display text-base font-semibold uppercase tracking-wide text-ink-muted dark:text-white/60">
        {label}
      </h2>
      <span className="text-primary-deep dark:text-primary">{icon}</span>
    </div>
    <p className="mt-4 font-display text-3xl font-bold text-ink dark:text-white">
      {value}
    </p>
    <p className="mt-2 text-sm text-ink-muted dark:text-white/50">{delta}</p>
  </div>
);

const Dashboard: React.FC = () => {
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Dashboard', href: '#' },
  ];

  return (
    <AdminLayout
      activeMenuItem="/admin/dashboard"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Main.Dashboard.View"
    >
      <div className="p-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Metric
            label="Total Sales"
            value="$25,340"
            delta="Compared to last week: +12%"
            icon={<TrendingUp className="h-6 w-6" />}
          />
          <Metric
            label="New Customers"
            value="1,250"
            delta="Compared to last week: +8%"
            icon={<UserPlus className="h-6 w-6" />}
          />
          <Metric
            label="Completed Orders"
            value="987"
            delta="Compared to last week: +5%"
            icon={<ShoppingCart className="h-6 w-6" />}
          />
        </div>

        {/* Sales Overview */}
        <div className="mt-8 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-white/5">
          <div className="border-b-2 border-primary px-6 py-4">
            <h2 className="font-display text-lg font-semibold text-ink dark:text-white">
              Sales Overview
            </h2>
          </div>
          <div className="p-6">
            <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 dark:border-white/15 dark:bg-white/5">
              <p className="text-sm text-ink-muted dark:text-white/50">
                [Placeholder for Sales Chart]
              </p>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-white/5">
          <div className="border-b-2 border-primary px-6 py-4">
            <h2 className="font-display text-lg font-semibold text-ink dark:text-white">
              Recent Activity
            </h2>
          </div>
          <ul className="divide-y divide-gray-100 px-6 dark:divide-white/10">
            <li className="flex items-center justify-between py-4">
              <div>
                <p className="font-semibold text-ink dark:text-white">
                  Order #12345
                </p>
                <p className="text-sm text-ink-muted dark:text-white/50">
                  Completed 2 hours ago
                </p>
              </div>
              <span className="font-semibold text-ink dark:text-white">
                $250.00
              </span>
            </li>
            <li className="flex items-center justify-between py-4">
              <div>
                <p className="font-semibold text-ink dark:text-white">
                  New Customer Signup
                </p>
                <p className="text-sm text-ink-muted dark:text-white/50">
                  3 hours ago
                </p>
              </div>
              <span className="font-semibold text-ink-muted dark:text-white/50">
                +1
              </span>
            </li>
            <li className="flex items-center justify-between py-4">
              <div>
                <p className="font-semibold text-ink dark:text-white">
                  Order #12344
                </p>
                <p className="text-sm text-ink-muted dark:text-white/50">
                  5 hours ago
                </p>
              </div>
              <span className="font-semibold text-ink dark:text-white">
                $180.00
              </span>
            </li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
